// src/components/TimeLogTable.js

import React, { useState, useEffect } from "react";
import { Edit, Trash2, CheckCircle, XCircle, ArrowUpDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import Select from "react-select";
import StudentProfileModal from "./StudentProfileModal";
import {
  getStoredData,
  setStoredData,
  updateStoredData,
} from "../lib/dataUtils";

const TimeLogStatus = ({ status }) => {
  return <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
};

const TimeLogsTable = ({
  timeLogs,
  onEdit,
  onDelete,
  students,
  opportunities,
}) => {
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [localTimeLogs, setLocalTimeLogs] = useState(timeLogs);
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const filterOptions = [
    {
      label: "Students",
      options: students.map((student) => ({
        value: `student-${student.studentId}`,
        label: `${student.firstName} ${student.lastName}`,
        type: "student",
        id: student.studentId,
      })),
    },
    {
      label: "Host Sites",
      options: opportunities.map((opportunity) => ({
        value: `host-${opportunity.id}`,
        label: opportunity.hostName,
        type: "host",
        id: opportunity.id,
      })),
    },
  ];

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.studentId === studentId);
    return student
      ? `${student.firstName} ${student.lastName}`
      : "Deleted student";
  };

  const getOpportunityName = (opportunityId) => {
    const opportunity = opportunities.find((o) => o.id === opportunityId);
    return opportunity ? opportunity.hostName : "Unknown";
  };

  const getOpportunityType = (opportunityId) => {
    const opportunity = opportunities.find((o) => o.id === opportunityId);
    return opportunity ? opportunity.opportunityType : "Unknown";
  };

  useEffect(() => {
    setLocalTimeLogs(timeLogs);
  }, [timeLogs]);

  useEffect(() => {
    const storedData = getStoredData("appData") || { timeLogs: [] };
    if (!storedData.timeLogs) {
      storedData.timeLogs = timeLogs;
      setStoredData("appData", storedData);
    }
  }, [timeLogs]);

  const filteredTimeLogs = localTimeLogs.filter((log) => {
    const matchesFilter = selectedFilter
      ? selectedFilter.type === "student"
        ? log.studentId === selectedFilter.id
        : log.opportunityId === selectedFilter.id
      : true;

    const matchesStatus = filterStatus === "" || log.status === filterStatus;

    return matchesFilter && matchesStatus;
  });

  const getSortedLogs = (logs) => {
    return [...logs].sort((a, b) => {
      switch (sortColumn) {
        case "studentName":
          const nameA = getStudentName(a.studentId).toLowerCase();
          const nameB = getStudentName(b.studentId).toLowerCase();
          return sortDirection === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);

        case "hostSite":
          const hostA = getOpportunityName(a.opportunityId).toLowerCase();
          const hostB = getOpportunityName(b.opportunityId).toLowerCase();
          return sortDirection === "asc"
            ? hostA.localeCompare(hostB)
            : hostB.localeCompare(hostA);

        case "opportunityType":
          const typeA = getOpportunityType(a.opportunityId).toLowerCase();
          const typeB = getOpportunityType(b.opportunityId).toLowerCase();
          return sortDirection === "asc"
            ? typeA.localeCompare(typeB)
            : typeB.localeCompare(typeA);

        case "date":
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;

        case "approval":
          return sortDirection === "asc"
            ? a.status.localeCompare(b.status)
            : b.status.localeCompare(a.status);

        default:
          return 0;
      }
    });
  };

  const handleSort = (column) => {
    setSortDirection((current) =>
      sortColumn === column ? (current === "asc" ? "desc" : "asc") : "desc"
    );
    setSortColumn(column);
  };

  const handleApproveTimeLog = (id) => {
    const updatedTimeLogs = localTimeLogs.map((log) => {
      if (log.id === id) {
        return { ...log, status: "approved" };
      }
      return log;
    });

    setLocalTimeLogs(updatedTimeLogs);
    updateStoredData("timeLogs", updatedTimeLogs);
  };

  const handleRejectTimeLog = (id) => {
    const updatedTimeLogs = localTimeLogs.map((log) => {
      if (log.id === id) {
        return { ...log, status: "rejected" };
      }
      return log;
    });

    setLocalTimeLogs(updatedTimeLogs);
    updateStoredData("timeLogs", updatedTimeLogs);
  };

  const handleStudentNameClick = (studentId) => {
    const student = getStudentById(studentId);
    setSelectedStudent({
      ...student,
      currentTimeLogs: localTimeLogs,
    });
  };

  const getStudentById = (studentId) => {
    return students.find((s) => s.studentId === studentId);
  };

  return (
    <>
      <div className="py-4 flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-grow">
          <Select
            isClearable
            options={filterOptions}
            value={selectedFilter}
            onChange={setSelectedFilter}
            placeholder="Search by student or host site..."
            classNamePrefix="select"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border px-3 py-2 rounded w-full md:w-1/3"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border">
        {/* Mobile view */}
        <div className="block md:hidden">
          {filteredTimeLogs.length > 0 ? (
            getSortedLogs(filteredTimeLogs).map((log) => (
              <div key={log.id} className="p-4 border-b last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <button
                    onClick={() => handleStudentNameClick(log.studentId)}
                    className="text-blue-600 hover:text-blue-900 underline"
                  >
                    {getStudentName(log.studentId)}
                  </button>
                  <div className="flex items-center space-x-2">
                    {log.status === "pending" ? (
                      <>
                        <CheckCircle
                          size={20}
                          className="text-green-500 cursor-pointer hover:text-green-600"
                          onClick={() => handleApproveTimeLog(log.id)}
                        />
                        <XCircle
                          size={20}
                          className="text-red-500 cursor-pointer hover:text-red-600"
                          onClick={() => handleRejectTimeLog(log.id)}
                        />
                      </>
                    ) : (
                      <span className="text-sm"><TimeLogStatus status={log.status} /></span>
                    )}
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Host Site:</span> {getOpportunityName(log.opportunityId)}</p>
                  <p><span className="text-gray-500">Type:</span> {getOpportunityType(log.opportunityId)}</p>
                  <p><span className="text-gray-500">Date:</span> {format(parseISO(log.date), "MMM dd, yyyy")}</p>
                  <p><span className="text-gray-500">Hours:</span> {log.startTime} - {log.endTime}</p>
                </div>
                <div className="mt-2 flex justify-end space-x-2">
                  <Edit
                    size={18}
                    className="text-blue-500 cursor-pointer hover:text-blue-600"
                    onClick={() => onEdit(log)}
                  />
                  <Trash2
                    size={18}
                    className="text-gray-500 cursor-pointer hover:text-gray-600"
                    onClick={() => onDelete(log.id)}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">No time logs found.</div>
          )}
        </div>
        {/* Desktop view */}
        <table className="hidden md:table min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                <button
                  onClick={() => handleSort("studentName")}
                  className="flex items-center space-x-1 hover:text-gray-700 transition-colors duration-200 w-full uppercase"
                >
                  <span>Student Name</span>
                  <ArrowUpDown
                    size={14}
                    className={`inline-block ${
                      sortColumn === "studentName"
                        ? "opacity-100"
                        : "opacity-40"
                    }`}
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                <button
                  onClick={() => handleSort("hostSite")}
                  className="flex items-center space-x-1 hover:text-gray-700 transition-colors duration-200 w-full uppercase"
                >
                  <span>Host Site</span>
                  <ArrowUpDown
                    size={14}
                    className={`inline-block ${
                      sortColumn === "hostSite" ? "opacity-100" : "opacity-40"
                    }`}
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                <button
                  onClick={() => handleSort("opportunityType")}
                  className="flex items-center space-x-1 hover:text-gray-700 transition-colors duration-200 w-full uppercase"
                >
                  <span>Opportunity Type</span>
                  <ArrowUpDown
                    size={14}
                    className={`inline-block ${
                      sortColumn === "opportunityType"
                        ? "opacity-100"
                        : "opacity-40"
                    }`}
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                <button
                  onClick={() => handleSort("date")}
                  className="flex items-center space-x-1 hover:text-gray-700 transition-colors duration-200 w-full uppercase"
                >
                  <span>Date</span>
                  <ArrowUpDown
                    size={14}
                    className={`inline-block ${
                      sortColumn === "date" ? "opacity-100" : "opacity-40"
                    }`}
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                <button
                  onClick={() => handleSort("approval")}
                  className="flex items-center space-x-1 hover:text-gray-700 transition-colors duration-200 w-full uppercase"
                >
                  <span>Approval</span>
                  <ArrowUpDown
                    size={14}
                    className={`inline-block ${
                      sortColumn === "approval" ? "opacity-100" : "opacity-40"
                    }`}
                  />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTimeLogs.length > 0 ? (
              getSortedLogs(filteredTimeLogs).map((log) => (
                <tr
                  key={log.id}
                  className={`${
                    log.status === "rejected"
                      ? "hover:bg-gray-100 transition duration-300 text-gray-400"
                      : "text-gray-700 hover:bg-gray-100 transition duration-300"
                  }`}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleStudentNameClick(log.studentId)}
                      className="text-blue-600 hover:text-blue-900 underline"
                    >
                      {getStudentName(log.studentId)}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {getOpportunityName(log.opportunityId)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {getOpportunityType(log.opportunityId)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {format(parseISO(log.date), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {log.startTime} - {log.endTime}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {log.status === "pending" ? (
                      <div className="flex items-center space-x-2">
                        <CheckCircle
                          size={20}
                          className="text-green-500 cursor-pointer hover:text-green-600 transition-colors duration-200"
                          onClick={() => handleApproveTimeLog(log.id)}
                        />
                        <XCircle
                          size={20}
                          className="text-red-500 cursor-pointer hover:text-red-600 transition-colors duration-200"
                          onClick={() => handleRejectTimeLog(log.id)}
                        />
                      </div>
                    ) : (
                      <TimeLogStatus status={log.status} />
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Edit
                        size={18}
                        className="text-blue-500 cursor-pointer hover:text-blue-600 transition-colors duration-200"
                        onClick={() => onEdit(log)}
                      />
                      <Trash2
                        size={18}
                        className="text-gray-500 cursor-pointer hover:text-gray-600 transition-colors duration-200"
                        onClick={() => onDelete(log.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="px-4 py-4 text-center text-sm text-gray-500"
                >
                  No time logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          opportunities={opportunities}
          timeLogs={localTimeLogs}
        />
      )}
    </>
  );
};

export default TimeLogsTable;
